// DamagesTableContact.tsx — Schäden-Reiter der Kontaktdetails im „Refined
// Classic"-Redesign (11.10.2026), Bauform wie die Schaden-Übersicht (/damages):
// Suche + „+ Schaden" in der Kopfleiste, darunter die DataTable mit Status-Pille,
// Spaltenfiltern, Sortierung und Spaltenmenü.
//
// Anders als /damages:
//  - lädt EINMAL alle Schäden des Kontakts (`damage/by-contact/:id`) und filtert
//    und sortiert im Browser — ein Kontakt hat wenige Schäden, und der
//    Endpunkt der Übersicht bleibt unberührt
//  - keine Kontakt-Spalte (wäre in jeder Zeile derselbe Name)
//  - „+ Schaden" legt den Schaden direkt für diesen Kontakt an
//  - eigene Spalten-Einstellungen nur für die Sitzung, nicht die der Übersicht
//  - Telefon (≤ 768 px): Karten wie in der Übersicht, Status als Auswahl

import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { I } from "./redesign/kvpIcons";
import {
  DataTable,
  ColumnsMenu,
  type Column,
  type DateRangeValue,
  type SortState,
  Dash,
} from "./redesign/table";
import "./redesign/contactRedesign.css";
import { useResponsive } from "../hooks/useResponsive";
import { SchadenKarten, StatusPill, isClosedStatus } from "./schaden/SchadenKarten";
import { apiFetch } from "../utils/apiFetch";
import { useWindowManager } from "./windowManager/WindowManagerContext";
import { openSchadenFormWindow } from "./windowManager/SchadenFormWindow";

/* ────────────────────── Typen & Abbildung ────────────────────── */

type SchadenZeile = {
  SchadenID: number;
  Status: string;
  Schadennr: string;
  Vertragsnummer: string;
  Sparte: string;
  Schadensart: string;
  RisikoOrt: string;
  /** Anzeige (Datum oder nur Jahr) … */
  SchadenDatum: string;
  MeldeDatum: string;
  /** … und der Wert zum Sortieren/Filtern (ISO yyyy-mm-dd, leer = unbekannt). */
  schadenIso: string;
  meldeIso: string;
};

/**
 * Importierte Schäden tragen nur ein Jahr (Datum auf 1.1. gesetzt) — dann nur
 * das Jahr zeigen, sonst das echte Datum; fällt auf das Jahr-Feld zurück.
 */
const fmtYearOrDate = (dateStr?: string | null, year?: number | null): string => {
  if (dateStr) {
    const d = new Date(dateStr);
    if (!Number.isNaN(d.getTime())) {
      const isYearOnly =
        d.getUTCMonth() === 0 && d.getUTCDate() === 1 && (!year || d.getUTCFullYear() === year);
      return isYearOnly ? String(year ?? d.getUTCFullYear()) : d.toLocaleDateString("de-DE");
    }
  }
  return year ? String(year) : "";
};

const isoVon = (dateStr?: string | null, year?: number | null): string => {
  if (dateStr) {
    const d = new Date(dateStr);
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  return year ? `${year}-01-01` : "";
};

const mapDamage = (d: any): SchadenZeile => ({
  SchadenID: Number(d.id ?? 0),
  Status: String(d.status ?? ""),
  Schadennr: String(d.damageNbr ?? d.externalDamageNbr ?? ""),
  Vertragsnummer: String(d.contract?.contractNbr ?? ""),
  Sparte: String(d.contract?.branch ?? ""),
  Schadensart: String(d.damageType ?? d.title ?? ""),
  RisikoOrt: String(d.riskObject?.name ?? d.contract?.riskLocation ?? ""),
  SchadenDatum: fmtYearOrDate(d.damageDate, d.damageYear),
  MeldeDatum: fmtYearOrDate(d.reportDate ?? d.created, d.reportYear),
  schadenIso: isoVon(d.damageDate, d.damageYear),
  meldeIso: isoVon(d.reportDate ?? d.created, d.reportYear),
});

/* ────────────────────── Filter & Sortierung (clientseitig) ────────────────────── */

const STATUS_FILTER_OPTIONS = ["Offen", "Geschlossen"];

/** Textfilter je Spalte: Teiltreffer, Groß-/Kleinschreibung egal. */
const TEXT_FILTER: Record<string, (r: SchadenZeile) => string> = {
  Schadennr: (r) => r.Schadennr,
  Vertragsnummer: (r) => `${r.Vertragsnummer} ${r.Sparte}`,
  Schadensart: (r) => r.Schadensart,
  RisikoOrt: (r) => r.RisikoOrt,
};

/** Sortierwert je Spalte; Datumsspalten sortieren nach ISO, nicht nach Anzeige. */
const SORT_WERT: Record<string, (r: SchadenZeile) => string> = {
  Status: (r) => (isClosedStatus(r.Status) ? "1" : "0"),
  Schadennr: (r) => r.Schadennr,
  Vertragsnummer: (r) => r.Vertragsnummer,
  Schadensart: (r) => r.Schadensart,
  RisikoOrt: (r) => r.RisikoOrt,
  SchadenDatum: (r) => r.schadenIso,
  MeldeDatum: (r) => r.meldeIso,
};

const DATUM: Record<string, (r: SchadenZeile) => string> = {
  SchadenDatum: (r) => r.schadenIso,
  MeldeDatum: (r) => r.meldeIso,
};

/** „TT.MM.JJJJ" oder ISO → ISO; leer, wenn nicht lesbar. */
const eingabeIso = (v?: string): string => {
  const t = (v ?? "").trim();
  if (!t) return "";
  const de = t.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (de) return `${de[3]}-${de[2].padStart(2, "0")}-${de[1].padStart(2, "0")}`;
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : "";
};

/* ────────────────────── Komponente ────────────────────── */

type DamagesTableContactProps = {
  kontaktId?: number;
  /** Für den Fenstertitel von „+ Schaden". */
  kontaktName?: string | null;
};

const DamagesTableContact: React.FC<DamagesTableContactProps> = ({ kontaktId, kontaktName }) => {
  const navigate = useNavigate();
  const { openWindow } = useWindowManager();
  const { isPhone } = useResponsive();
  const hatKontakt = Number.isFinite(kontaktId) && Number(kontaktId) > 0;

  const [alle, setAlle] = useState<SchadenZeile[]>([]);
  const [loading, setLoading] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, string | boolean | undefined>>({});
  const [dateRangeValues, setDateRangeValues] = useState<Record<string, DateRangeValue | undefined>>({});
  const [sort, setSort] = useState<SortState | null>(null);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [collapsedColumns, setCollapsedColumns] = useState<string[]>([]);

  useEffect(() => {
    if (!hatKontakt) {
      setAlle([]);
      return;
    }
    let aktiv = true;
    setLoading(true);
    setFehler(null);
    apiFetch<any[]>(`damage/by-contact/${Number(kontaktId)}`)
      .then(({ data }) => {
        if (aktiv) setAlle((Array.isArray(data) ? data : []).map(mapDamage));
      })
      .catch(() => {
        if (aktiv) {
          setAlle([]);
          setFehler("Die Schäden konnten nicht geladen werden.");
        }
      })
      .finally(() => {
        if (aktiv) setLoading(false);
      });
    return () => {
      aktiv = false;
    };
  }, [hatKontakt, kontaktId]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = alle.filter((r) => {
      if (
        q &&
        ![r.Schadennr, r.Vertragsnummer, r.Sparte, r.Schadensart, r.RisikoOrt].some((v) =>
          v.toLowerCase().includes(q),
        )
      )
        return false;
      const st = filterValues.Status;
      if (st && (st === "Geschlossen") !== isClosedStatus(r.Status)) return false;
      for (const [id, get] of Object.entries(TEXT_FILTER)) {
        const v = filterValues[id];
        if (typeof v === "string" && v.trim() && !get(r).toLowerCase().includes(v.trim().toLowerCase()))
          return false;
      }
      for (const [id, get] of Object.entries(DATUM)) {
        const range = dateRangeValues[id];
        if (!range) continue;
        const von = eingabeIso(range.von);
        const bis = eingabeIso(range.bis);
        const wert = get(r);
        if ((von || bis) && !wert) return false;
        if (von && wert < von) return false;
        if (bis && wert > bis) return false;
      }
      return true;
    });
    const get = sort ? SORT_WERT[sort.field] : undefined;
    if (sort && get) {
      const dir = sort.direction === "asc" ? 1 : -1;
      list = [...list].sort((a, b) => {
        const va = get(a);
        const vb = get(b);
        // Leere Werte immer ans Ende, unabhängig von der Richtung.
        if (!va && vb) return 1;
        if (va && !vb) return -1;
        return va.localeCompare(vb, "de", { numeric: true }) * dir;
      });
    } else {
      // Standard: jüngster Schaden oben.
      list = [...list].sort((a, b) => b.schadenIso.localeCompare(a.schadenIso) || b.SchadenID - a.SchadenID);
    }
    return list;
  }, [alle, search, filterValues, dateRangeValues, sort]);

  const updateFilter = useCallback((columnId: string, value: string | boolean | undefined) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      if (value === undefined || value === "" || value === false) delete next[columnId];
      else next[columnId] = value;
      return next;
    });
  }, []);

  const updateDateRange = useCallback((columnId: string, next: DateRangeValue) => {
    setDateRangeValues((prev) => {
      const merged = { ...prev };
      if (!next.von?.trim() && !next.bis?.trim()) delete merged[columnId];
      else merged[columnId] = next;
      return merged;
    });
  }, []);

  const toggleCollapsed = useCallback(
    (id: string) =>
      setCollapsedColumns((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])),
    [],
  );

  const columns: Column<SchadenZeile>[] = useMemo(
    () => [
      {
        id: "Status",
        header: "Status",
        pinned: true,
        frozen: true,
        width: 140,
        minWidth: 118,
        sortable: true,
        filter: { kind: "dropdown", options: STATUS_FILTER_OPTIONS },
        cell: (r) => <StatusPill status={r.Status} />,
      },
      {
        id: "Schadennr",
        header: "Schaden-Nr.",
        className: "name",
        pinned: true,
        frozen: true,
        width: 165,
        minWidth: 120,
        resizable: true,
        sortable: true,
        filter: { kind: "text" },
        cell: (r) => r.Schadennr || <Dash />,
      },
      {
        id: "Vertragsnummer",
        header: "Vertrag",
        className: "kt-wrap",
        minWidth: 150,
        resizable: true,
        sortable: true,
        filter: { kind: "text" },
        hideable: true,
        collapsible: true,
        cell: (r) =>
          r.Vertragsnummer || r.Sparte ? (
            <span>
              {r.Vertragsnummer || <Dash />}
              {r.Sparte && (
                <span style={{ display: "block", fontSize: 12, color: "var(--ink-3)" }}>{r.Sparte}</span>
              )}
            </span>
          ) : (
            <Dash />
          ),
      },
      {
        id: "Schadensart",
        header: "Schadensart",
        className: "kt-wrap",
        minWidth: 150,
        resizable: true,
        sortable: true,
        filter: { kind: "text" },
        hideable: true,
        collapsible: true,
        cell: (r) => r.Schadensart || <Dash />,
      },
      {
        id: "RisikoOrt",
        header: "Risiko / Ort",
        className: "kt-wrap",
        minWidth: 170,
        resizable: true,
        sortable: true,
        filter: { kind: "text" },
        hideable: true,
        collapsible: true,
        cell: (r) => r.RisikoOrt || <Dash />,
      },
      {
        id: "SchadenDatum",
        header: "Schadentag",
        width: 130,
        minWidth: 100,
        resizable: true,
        sortable: true,
        filter: { kind: "dateRange" },
        hideable: true,
        collapsible: true,
        cell: (r) => r.SchadenDatum || <Dash />,
      },
      {
        id: "MeldeDatum",
        header: "Meldedatum",
        width: 130,
        minWidth: 100,
        resizable: true,
        sortable: true,
        filter: { kind: "dateRange" },
        hideable: true,
        collapsible: true,
        cell: (r) => r.MeldeDatum || <Dash />,
      },
    ],
    [],
  );

  const offen = alle.filter((r) => !isClosedStatus(r.Status)).length;
  const subBar = (
    <div className="kt-subbar">
      <span style={{ marginRight: "auto", fontSize: 12.5, fontWeight: 600, color: "var(--ink-3)" }}>
        {loading
          ? ""
          : `${alle.length} ${alle.length === 1 ? "Schaden" : "Schäden"}${
              alle.length ? ` · ${offen} offen` : ""
            }${rows.length !== alle.length ? ` · ${rows.length} angezeigt` : ""}`}
      </span>
      {isPhone ? (
        <select
          className="sel"
          style={{ height: 34, width: "auto", minWidth: 120, padding: "0 30px 0 11px", fontSize: 13.5 }}
          value={typeof filterValues.Status === "string" ? filterValues.Status : ""}
          onChange={(e) => updateFilter("Status", e.target.value || undefined)}
          aria-label="Status"
        >
          <option value="">Alle Status</option>
          {STATUS_FILTER_OPTIONS.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : (
        <ColumnsMenu
          columns={columns}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          collapsedColumns={collapsedColumns}
          onToggleCollapsed={toggleCollapsed}
        />
      )}
    </div>
  );

  const leerText = loading
    ? "Lade Schäden …"
    : alle.length === 0
      ? "Für diesen Kontakt ist kein Schaden erfasst."
      : "Keine Schäden für diese Filter.";

  return (
    <div className="kvp-rd kvp-rd-table-page">
      <div className="bar">
        <div className="search">
          {I.search}
          <input
            placeholder="Nach Schaden-Nr., Vertrag, Schadensart suchen …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="tbtn primary"
          disabled={!hatKontakt}
          onClick={() =>
            openSchadenFormWindow(openWindow, {
              contactId: hatKontakt ? Number(kontaktId) : null,
              contactName: kontaktName ?? null,
            })
          }
        >
          {I.plus}Schaden
        </button>
      </div>

      {fehler && <div className="rd-error">{fehler}</div>}

      {isPhone ? (
        <>
          {subBar}
          <SchadenKarten
            karten={rows.map((r) => ({
              id: r.SchadenID,
              status: r.Status,
              schadennr: r.Schadennr,
              vertrag: r.Vertragsnummer,
              sparte: r.Sparte,
              schadensart: r.Schadensart,
              risikoOrt: r.RisikoOrt,
              schadenDatum: r.SchadenDatum,
              meldeDatum: r.MeldeDatum,
            }))}
            onOpen={(id) => navigate(`/schaeden/${id}`)}
            emptyText={leerText}
          />
        </>
      ) : (
      <DataTable<SchadenZeile>
        columns={columns}
        rows={rows}
        rowKey={(r) => r.SchadenID}
        onRowClick={(r) => navigate(`/schaeden/${r.SchadenID}`)}
        emptyText={leerText}
        sort={sort}
        onSortChange={setSort}
        filterValues={filterValues}
        onFilterChange={updateFilter}
        dateRangeValues={dateRangeValues}
        onDateRangeChange={updateDateRange}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        columnWidths={columnWidths}
        onColumnWidthsChange={setColumnWidths}
        collapsedColumns={collapsedColumns}
        onToggleCollapsed={toggleCollapsed}
        loading={loading}
        loadingText="Lade Schäden …"
        aboveTable={subBar}
      />
      )}
    </div>
  );
};

export default DamagesTableContact;
